/**
 * useInAppBrowser — detectează dacă userul e într-un in-app browser
 * (Instagram, Facebook, TikTok, Twitter, LinkedIn, Messenger, etc).
 *
 * Aplicațiile mobile folosesc WebView cu cookies/localStorage izolate,
 * deci sesiunea nu e partajată cu Safari/Chrome. Recomandăm userului
 * să deschidă în browserul extern.
 *
 * Pattern de detectare: User-Agent conține token specific app-ului.
 */

interface InAppInfo {
  /** True dacă e detectat ca in-app browser. */
  inApp: boolean;
  /** Numele app-ului detectat (sau null dacă e browser normal). */
  app: string | null;
  /** Instrucțiuni scurte pt user despre cum să deschidă în browser extern. */
  hint: string | null;
}

const APP_PATTERNS: Array<{ name: string; pattern: RegExp; hint: string }> = [
  { name: 'Instagram', pattern: /Instagram/i, hint: 'Instagram: apasă ⋮ (colț dreapta sus) → „Deschide în Safari" sau „Open in Browser".' },
  { name: 'Facebook', pattern: /FBAN|FBAV/i, hint: 'Facebook: apasă ⋮ (colț dreapta sus) → „Deschide în browser".' },
  { name: 'Messenger', pattern: /Messenger/i, hint: 'Messenger: apasă ⋮ (colț dreapta sus) → „Deschide în browser".' },
  { name: 'TikTok', pattern: /TikTok|musical_ly/i, hint: 'TikTok: apasă ⋮ (colț dreapta sus) → „Open in browser".' },
  { name: 'Twitter', pattern: /Twitter/i, hint: 'Twitter/X: apasă ⋮ (colț dreapta sus) → „Open in browser".' },
  { name: 'LinkedIn', pattern: /LinkedInApp/i, hint: 'LinkedIn: apasă ⋮ (colț dreapta sus) → „Open in browser".' },
  { name: 'WeChat', pattern: /MicroMessenger/i, hint: 'WeChat: apasă ⋮ din colțul dreapta sus → „Open in Browser".' },
  { name: 'Line', pattern: /Line/i, hint: 'LINE: apasă ⋮ → „Open in browser".' },
  { name: 'Snapchat', pattern: /Snapchat/i, hint: 'Snapchat: apasă pe link lung și alege „Open in browser".' },
  { name: 'Pinterest', pattern: /Pinterest/i, hint: 'Pinterest: apasă ⋮ → „Open in browser".' },
  { name: 'Google Search App', pattern: /GSA|iPhone;.*Google/i, hint: 'Google App: apasă ⋮ → „Open in browser".' },
];

export function detectInAppBrowser(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): InAppInfo {
  for (const { name, pattern, hint } of APP_PATTERNS) {
    if (pattern.test(ua)) {
      return { inApp: true, app: name, hint };
    }
  }
  return { inApp: false, app: null, hint: null };
}

export function useInAppBrowser(): InAppInfo {
  if (typeof navigator === 'undefined') {
    return { inApp: false, app: null, hint: null };
  }
  return detectInAppBrowser(navigator.userAgent);
}

interface OpenInBrowserResult {
  /** True daca s-a putut initia intent-ul (Android). False pe iOS. */
  attempted: boolean;
  /** Mesaj scurt pt user despre ce urmeaza. */
  message: string;
}

/**
 * Încearcă să deschidă URL-ul curent în browserul extern (Chrome/Safari).
 *
 - pe Android: foloseste intent:// URL cu Chrome ca default — Instagram permite
   (de obicei) si daca nu, userul vede un chooser.
 - pe iOS: nu exista API public. Copiem URL-ul in clipboard si aratam
   instructiuni. Userul trebuie sa faca tap manual pe bannerul ⋮ → "Open in Safari".
 *
 * Nota: returneaza imediat; rezultatul intent-ului nu e detectabil din JS.
 */
export function tryOpenInExternalBrowser(): OpenInBrowserResult {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { attempted: false, message: 'Browser nedetectat' };
  }
  const url = window.location.href;
  const ua = navigator.userAgent;

  // Android: intent:// merge pentru multe app-uri (inclusiv Instagram uneori)
  if (/Android/i.test(ua)) {
    try {
      // Strip protocol si rebuild ca intent://
      const noProto = url.replace(/^https?:\/\//, '');
      window.location.href = `intent://${noProto}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
      return { attempted: true, message: 'Se deschide în Chrome...' };
    } catch {
      return { attempted: false, message: 'Nu s-a putut deschide automat' };
    }
  }

  // iOS: copiaza URL in clipboard, userul face manual
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).catch(() => {});
    } else {
      // Fallback pt browsere vechi
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    return { attempted: false, message: 'Link copiat. Apasă ⋮ → "Deschide în Safari"' };
  } catch {
    return { attempted: false, message: 'Copiaza link-ul manual: ' + url };
  }
}